import { useAdminAuth, type EffectivePlatformRole } from '@/context/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import { useLocation, Link, Navigate } from 'react-router-dom';
import {
  LayoutDashboard, Store, BarChart3, LogOut, ChevronLeft, ChevronRight, Shield, Tag, TicketCheck, Package, FileText, Menu, Users, Settings, AlertTriangle, Megaphone, Inbox, Percent, FileSignature, Receipt, Banknote
} from 'lucide-react';
import { useAdminInvoices } from '@/hooks/use-invoices';
import { useState } from 'react';
import { cn } from '@/lib/utils';

interface NavGroup {
  label: string;
  items: { to: string; icon: React.ElementType; label: string; labelKey?: string; roles: EffectivePlatformRole[] }[];
}

const navGroups: NavGroup[] = [
  {
    label: 'misc_nav.main',
    items: [
      { to: '/admin/dashboard', icon: LayoutDashboard, label: 'misc_nav.home', roles: ['platform_owner', 'support_admin', 'finance_admin', 'auditor'] },
      { to: '/admin/merchants', icon: Store, label: 'misc_nav.merchants', roles: ['platform_owner', 'support_admin', 'auditor'] },
    ],
  },
  {
    label: 'misc_nav.catalog',
    items: [
      { to: '/admin/products', icon: Package, label: 'misc_nav.products', roles: ['platform_owner', 'support_admin'] },
    ],
  },
  {
    label: 'misc_nav.finance',
    items: [
      { to: '/admin/pricing', icon: Tag, label: 'misc_nav.markups', roles: ['platform_owner', 'finance_admin'] },
      { to: '/admin/invoices', icon: FileSignature, label: 'Fakturalar', labelKey: 'invoice.nav', roles: ['platform_owner', 'finance_admin', 'auditor'] },
      { to: '/admin/fiscal-receipts', icon: Receipt, label: 'Fiskal cheklar', labelKey: 'fiscal.admin.nav', roles: ['platform_owner', 'finance_admin', 'auditor'] },
      { to: '/admin/merchant-payouts', icon: Banknote, label: 'adm_payouts.nav', labelKey: 'adm_payouts.nav', roles: ['platform_owner', 'finance_admin', 'auditor'] },
      { to: '/admin/contracts', icon: FileText, label: 'misc_nav.contracts', roles: ['platform_owner', 'support_admin', 'finance_admin', 'auditor'] },
      { to: '/admin/reports', icon: BarChart3, label: 'misc_nav.reports', roles: ['platform_owner', 'finance_admin', 'auditor'] },
    ],
  },
  {
    label: 'misc_nav.marketing',
    items: [
      { to: '/admin/marketing/dashboard', icon: Megaphone, label: 'misc_nav.marketing', roles: ['platform_owner', 'support_admin', 'finance_admin', 'auditor'] },
      { to: '/admin/marketing/campaigns', icon: Megaphone, label: 'misc_nav.campaigns', roles: ['platform_owner', 'support_admin', 'finance_admin', 'auditor'] },
      { to: '/admin/marketing/applications', icon: Inbox, label: 'misc_nav.applications', roles: ['platform_owner', 'support_admin'] },
      { to: '/admin/marketing/placements/vip', icon: Megaphone, label: 'misc_nav.vip', roles: ['platform_owner', 'support_admin'] },
      { to: '/admin/marketing/placements/categories', icon: Megaphone, label: 'misc_nav.categories', roles: ['platform_owner', 'support_admin'] },
      { to: '/admin/marketing/placements/popular', icon: Megaphone, label: 'misc_nav.popular', roles: ['platform_owner', 'support_admin'] },
      { to: '/admin/marketing/placements/big-cashback', icon: Percent, label: 'misc_nav.big_cashback', roles: ['platform_owner', 'finance_admin', 'support_admin'] },
      { to: '/admin/marketing/cashback', icon: Percent, label: 'misc_nav.cashback', roles: ['platform_owner', 'finance_admin'] },
      { to: '/admin/marketing/calendar', icon: Megaphone, label: 'misc_nav.calendar', roles: ['platform_owner', 'support_admin', 'finance_admin', 'auditor'] },
      { to: '/admin/marketing/history', icon: Megaphone, label: 'misc_nav.history', roles: ['platform_owner', 'support_admin', 'finance_admin', 'auditor'] },
      { to: '/admin/marketing/settings', icon: Percent, label: 'misc_nav.settings', roles: ['platform_owner', 'support_admin', 'finance_admin', 'auditor'] },
    ],
  },
  {
    label: 'misc_nav.support',
    items: [
      { to: '/admin/tickets', icon: TicketCheck, label: 'misc_nav.tickets', roles: ['platform_owner', 'support_admin'] },
    ],
  },
  {
    label: 'misc_nav.settings',
    items: [
      { to: '/admin/platform-users', icon: Users, label: 'misc_nav.admins', roles: ['platform_owner'] },
      { to: '/admin/settings', icon: Settings, label: 'misc_nav.requisites', roles: ['platform_owner', 'finance_admin'] },
      { to: '/admin/errors', icon: AlertTriangle, label: 'misc_nav.errors', roles: ['platform_owner', 'support_admin'] },
    ],
  },
];

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const { admin, loading, logout } = useAdminAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const canSeeInvoices = !!admin && ['platform_owner', 'finance_admin', 'auditor'].includes(admin.role);
  const { data: invoiceInbox } = useAdminInvoices({ tab: 'new' }, canSeeInvoices);
  const pendingInvoices = invoiceInbox?.pending ?? 0;

  if (loading) {
    return <div className="flex items-center justify-center h-screen text-muted-foreground">{t('misc_common.loading')}</div>;
  }

  if (!admin) {
    return <Navigate to="/admin/login" replace />;
  }

  const filteredGroups = navGroups
    .map(g => ({ ...g, items: g.items.filter(item => item.roles.includes(admin.role)) }))
    .filter(g => g.items.length > 0);

  const sidebarContent = (
    <>
      <div className="flex items-center justify-between h-14 px-4 border-b border-border">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-primary" />
            <span className="font-semibold text-foreground text-sm tracking-tight">{t('misc_nav.admin_panel')}</span>
          </div>
        )}
        <button onClick={() => { setCollapsed(!collapsed); setMobileOpen(false); }} className="p-1 rounded-md hover:bg-accent text-muted-foreground hidden md:block">
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
        <button onClick={() => setMobileOpen(false)} className="p-1 rounded-md hover:bg-accent text-muted-foreground md:hidden">
          <ChevronLeft size={16} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {filteredGroups.map((group, gi) => (
          <div key={group.label} className={cn(gi > 0 && 'mt-4')}>
            {!collapsed && (
              <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                {t(group.label)}
              </p>
            )}
            {collapsed && gi > 0 && <div className="mx-3 mb-2 border-t border-border" />}
            <div className="space-y-0.5">
              {group.items.map(item => {
                const active = location.pathname.startsWith(item.to);
                const label = item.labelKey ? t(item.labelKey) : t(item.label);
                const count = item.to === '/admin/invoices' ? pendingInvoices : 0;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                      active
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    )}
                    title={collapsed ? label : undefined}
                  >
                    <item.icon size={18} />
                    {!collapsed && <span className="flex-1">{label}</span>}
                    {!collapsed && count > 0 && (
                      <span className={cn('rounded-full px-1.5 text-[10px] font-semibold', active ? 'bg-primary-foreground text-primary' : 'bg-primary text-primary-foreground')}>{count}</span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-border px-2 py-2 space-y-0.5">
        {!collapsed && admin && (
          <div className="px-3 py-2">
            <p className="text-xs font-medium text-foreground truncate">{admin.full_name}</p>
            <p className="text-xs text-muted-foreground">{t(`admin_roles.${admin.isDirector ? 'platform_director' : admin.role}.label`)}</p>
          </div>
        )}
        <button onClick={logout} className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-destructive hover:bg-destructive/10 transition-colors">
          <LogOut size={16} />
          {!collapsed && <span>{t('common.logout')}</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-background">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Desktop sidebar */}
      <aside className={cn(
        "hidden md:flex flex-col border-r border-border bg-card transition-all duration-200",
        collapsed ? "w-16" : "w-60"
      )}>
        {sidebarContent}
      </aside>

      {/* Mobile sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 flex flex-col w-60 border-r border-border bg-card transition-transform duration-200 md:hidden",
        mobileOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        {sidebarContent}
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <div className="h-12 flex items-center border-b border-border px-4 md:hidden">
          <button onClick={() => setMobileOpen(true)} className="p-1 rounded-md hover:bg-accent text-muted-foreground">
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2 ml-3">
            <Shield size={16} className="text-primary" />
            <span className="font-semibold text-foreground text-sm">{t('misc_nav.admin_panel')}</span>
          </div>
        </div>

        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
